import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../../hooks/useAccounts';
import { accountsService, AccountFormData } from '../../services/accountsService';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

const AccountForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { businessId } = useAuth();
  const { account, loading: accountLoading } = useAccount(id || null);

  const [accountId, setAccountId] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<'cash' | 'bank' | 'other'>('cash');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize form with existing account data or generate account ID for new accounts
  useEffect(() => {
    if (account) {
      setAccountId(account.account_id);
      setName(account.name);
      setType(account.type as 'cash' | 'bank' | 'other');
      setBankName(account.bank_name || '');
      setAccountNumber(account.account_number || '');
      setAccountHolderName(account.account_holder_name || '');
    } else if (!id && businessId) {
      // Generate account ID for new accounts
      const generateId = async () => {
        try {
          const generatedId = await accountsService.generateAccountId(businessId);
          setAccountId(generatedId);
        } catch (error) {
          console.error('Error generating account ID:', error);
        }
      };
      generateId();
    }
  }, [account, id, businessId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      const accountData: AccountFormData = {
        account_id: accountId,
        name,
        type,
        bank_name: type === 'bank' ? bankName : undefined,
        account_number: type === 'bank' ? accountNumber : undefined,
        account_holder_name: type === 'bank' ? accountHolderName : undefined,
      };

      let result;
      if (id) {
        // Update existing account
        result = await accountsService.updateAccount(id, accountData);
      } else {
        // Create new account
        result = await accountsService.createAccount(accountData, businessId || undefined);
      }

      if (result.error) {
        setError(result.error.message);
      } else {
        // Redirect to account details page
        const accountId = id || result.account?.id;
        navigate(`/accounts/${accountId}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save account');
    }

    setLoading(false);
  };

  if (accountLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {id ? 'Edit Account' : 'Add New Account'}
        </h1>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Input
              label="Account ID"
              type="text"
              value={accountId}
              readOnly
              className="bg-gray-50"
              required
            />

            <Input
              label="Account Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="mt-6">
            <Select
              label="Account Type"
              value={type}
              onChange={(e) => setType(e.target.value as any)}
              options={[
                { value: 'cash', label: 'Cash' },
                { value: 'bank', label: 'Bank' },
                { value: 'other', label: 'Other' },
              ]}
              required
            />
          </div>

          {/* Bank-specific fields - only show when type is 'bank' */}
          {type === 'bank' && (
            <div className="mt-6 space-y-6">
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Details</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Input
                    label="Bank Name"
                    type="text"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    placeholder="e.g., Bank Central Asia"
                  />

                  <Input
                    label="Account Number"
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="e.g., 1234567890"
                  />
                </div>

                <div className="mt-6">
                  <Input
                    label="Account Holder Name"
                    type="text"
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    placeholder="e.g., John Doe"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/accounts')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountForm;